import theme from 'config/theme';
import {Icon} from 'interface/base/Icon';
import {WorkspaceFrame} from 'interface/stacks/workspace/base/WorkspaceFrame';
import {useCreateNewChat} from 'interface/stacks/workspace/layout/WorkspaceAiChat/hooks/useCreateNewChat';
import {useDragDropChatTarget} from 'interface/stacks/workspace/storage/hooks/useDragDropChatTarget';
import {useEffect, useRef, useState} from 'react';
import {
  type LayoutChangeEvent,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import {useDispatch, useSelector} from 'react-redux';
import * as aichat from 'store/slices/aichat';
import type {EntityChat} from 'store/slices/aichat/types';
import type {FilesItem} from 'store/slices/files/types';
import type {Member} from 'store/slices/settings/types';
import {AiChatHeader} from '../components/AiChatHeader';
import {AiChatGettingStarted} from '../components/gettingStarted/AiChatGettingStarted';
import {AiChatInput} from '../components/input/AiChatInput';
import {AiChatMessageItem} from '../components/messages/AiChatMessageItem';
import {AiChatMessageSkeleton} from '../components/skeletons/AiChatMessageSkeleton';
import {
  AICHAT_TOOLBAR_HEIGHT,
  CHAT_POLL_INTERVAL,
  NEW_CHAT,
  NEW_PRIVATE_CHAT,
} from '../consts';
import {AttachAndScopeProvider} from '../context/AttachAndScopeContext';
import {CurrentFolderProvider} from '../context/CurrentFolderContext';
import {type FilterInfo, getFilterInfoFromItems} from '../helpers';
import {useCreateNewMessage} from '../hooks/useCreateNewChatMessage';
import {useFetchSingleChatData} from '../hooks/useFetchSingleChatData';

interface Props {
  namespace: string;
  instanceId: string;
  instanceName: string;
  chatId: string;
  messageId: string;
  onOpenSidebar: () => void;
  onNewChatPress: (isPrivate: boolean) => void;
  workspaceMembers: Member[];
  showMenu: boolean;
}

export function AiChat({
  namespace,
  instanceId,
  instanceName,
  chatId,
  messageId,
  workspaceMembers,
  onOpenSidebar,
  onNewChatPress,
  showMenu,
}: Props) {
  const dispatch = useDispatch();
  const [firstLoad, setFirstLoad] = useState(true);

  const chats = useSelector(aichat.selectors.getChats(namespace, instanceId));
  const hasChats = chats.length > 0;

  const {
    isLoading: chatLoading,
    loadFailed: chatLoadFailed,
    fetchChatData,
  } = useFetchSingleChatData(namespace, instanceId, instanceName);
  const {isLoading: createMessageLoading, createChatMessage} =
    useCreateNewMessage();
  const {isLoading: createChatLoading, createChat} = useCreateNewChat();
  const [showBottomButton, setShowBottomButton] = useState(false);
  const scrollMinHeightRef = useRef<number>(0);
  const lastMessageIdRef = useRef<string | null>(null);

  const chat: EntityChat = useSelector(
    aichat.selectors.getChat(namespace, instanceId, chatId),
  );
  const messages = chat?.messages ?? [];
  const messageCount = chat?.messageCount ?? 0;

  const ref = useRef<View>(null);
  const {isDropping} = useDragDropChatTarget({
    ref,
  });

  const isNewChat =
    chatId === null || chatId === NEW_CHAT || chatId === NEW_PRIVATE_CHAT;
  const isPrivate =
    chat?.privacy?.visibility === 'private' || chatId === NEW_PRIVATE_CHAT;

  const lastMessage = messages[messages.length - 1];
  const lastMessageId = lastMessage?.id;

  const isEnabled =
    !lastMessage ||
    lastMessage?.state === 'complete' ||
    lastMessage?.state === 'errored';
  const scrollViewRef = useRef<ScrollView>(null);

  if (!lastMessageIdRef.current) {
    lastMessageIdRef.current = lastMessage?.id;
  }
  const scrollMinHeight =
    !lastMessageIdRef.current || lastMessageIdRef.current !== lastMessageId
      ? scrollMinHeightRef.current
      : undefined;

  const scrollToMessage = (messageId: string) => {
    // TODO: AICHAT: Fix this
    const messageIndex = messages.findIndex(
      message => message.id === messageId,
    );
    if (messageIndex !== -1) {
      scrollViewRef.current?.scrollTo({y: messageIndex * 30, animated: true});
    }
  };

  const scrollToBottom = () => {
    scrollViewRef.current?.scrollToEnd({animated: true});
  };

  const onScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const scrollY = event.nativeEvent.contentOffset.y;
    const contentHeight = event.nativeEvent.contentSize.height;
    const viewportHeight = event.nativeEvent.layoutMeasurement.height;
    const tolerance = 50;

    const showButton = scrollY + viewportHeight < contentHeight - tolerance;
    setShowBottomButton(showButton);
  };

  const submitQuestion = (question: string, attachFiles: FilesItem[]) => {
    createMessageOrNewChat(
      question,
      false,
      false,
      getFilterInfoFromItems([]),
      getFilterInfoFromItems(attachFiles),
    );
  };

  const createMessageOrNewChat = async (
    message: string,
    webSearch: boolean,
    withRag: boolean,
    scopeFiles: FilterInfo[],
    attachFiles: FilterInfo[],
  ) => {
    if (isNewChat) {
      setFirstLoad(false);
      return await createChat(
        message,
        'detailed',
        isPrivate,
        scopeFiles,
        attachFiles,
      );
    }
    return await createChatMessage(
      chatId,
      message,
      'detailed',
      scopeFiles,
      attachFiles,
    );
  };

  useEffect(() => {
    if (messageId) {
      scrollToMessage(messageId);
    }
  }, [messageId]);

  const shouldPoll =
    !isNewChat &&
    (chat?.status !== 'ready' ||
      !['complete', 'errored'].includes(lastMessage?.state));

  const onScrollLayout = (event: LayoutChangeEvent) => {
    scrollMinHeightRef.current = event.nativeEvent.layout.height - 66 - 66;
  };

  useEffect(() => {
    const interval = setInterval(() => {
      if (shouldPoll) {
        fetchChatData(chatId);
      }
    }, CHAT_POLL_INTERVAL);

    return () => clearInterval(interval);
  }, [shouldPoll]);

  useEffect(() => {
    setFirstLoad(true);
    if (isNewChat) {
      return;
    }
    const fetchData = async () => {
      await fetchChatData(chatId);
      setFirstLoad(false);
    };
    fetchData();
  }, [instanceId, chatId]);

  useEffect(() => {
    if (lastMessage?.state === 'complete' || lastMessage?.state === 'errored') {
      dispatch(aichat.default.actions.setTriggerStreaming(null));
    }
    scrollToBottom();
  }, [chatId, lastMessageId, lastMessage?.state]);

  const hasChatCached = chat?.messages?.length > 0;
  const showSkeleton = !hasChatCached && chatLoading && firstLoad;
  const showGettingStarted = !showSkeleton && messageCount === 0;
  const showError = chatLoadFailed && messageCount !== chat?.messages?.length;

  return (
    <AttachAndScopeProvider>
      <View style={styles.root}>
        <View style={styles.container}>
          {showGettingStarted ? (
            <AiChatGettingStarted submitDirectQuestion={submitQuestion} />
          ) : (
            <View
              style={{
                flex: 1,
                maxWidth: 800,
                width: '100%',
                alignSelf: 'center',
              }}>
              <ScrollView
                ref={scrollViewRef}
                style={styles.messagesContainer}
                contentContainerStyle={styles.scrollContent}
                scrollEventThrottle={16}
                onScroll={onScroll}
                onLayout={onScrollLayout}
                showsVerticalScrollIndicator={false}>
                <View style={styles.messages}>
                  {showSkeleton ? (
                    <AiChatMessageSkeleton />
                  ) : showError ? (
                    <AiChatMessageSkeleton />
                  ) : (
                    messages.map((message, index) => {
                      const isLastMessage = index === messages.length - 1;
                      return (
                        <AiChatMessageItem
                          key={message.id}
                          message={message}
                          isLastMessage={isLastMessage}
                          fixedMinHeight={
                            isLastMessage ? scrollMinHeight : undefined
                          }
                        />
                      );
                    })
                  )}
                </View>
              </ScrollView>
              {showBottomButton && (
                <Pressable
                  style={styles.bottomButtonContainer}
                  onPress={scrollToBottom}>
                  <Icon
                    name="lucide:arrow-down"
                    size={20}
                    color={theme.colors.neutral.$700}
                  />
                </Pressable>
              )}
            </View>
          )}
          <View style={styles.headerContainer}>
            <AiChatHeader
              workspaceId={instanceId}
              chatId={chatId}
              isPrivate={isPrivate}
              showMenu={showMenu}
              title={chat?.name ?? 'New chat'}
              onOpenSidebar={onOpenSidebar}
              onNewChatPress={onNewChatPress}
              workspaceMembers={workspaceMembers}
            />
          </View>

          <CurrentFolderProvider>
            <AiChatInput
              chatId={chatId}
              onSendMessage={createMessageOrNewChat}
              isEnabled={isEnabled}
              enableSettings={true}
              style={styles.inputContainer}
            />
          </CurrentFolderProvider>
        </View>
        {isDropping && (
          <WorkspaceFrame mode="upload" color={theme.colors.brand.$4Base} />
        )}
      </View>
    </AttachAndScopeProvider>
  );
}

const styles = StyleSheet.create({
  root: {
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    backgroundColor: theme.colors.neutral.$16,
  },
  container: {
    flex: 1,
    width: '100%',
  },
  headerContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: AICHAT_TOOLBAR_HEIGHT,
    // overflow: 'hidden',
  },
  messagesContainer: {
    flex: 1,
    padding: 16,
    paddingTop: AICHAT_TOOLBAR_HEIGHT + 16,
    paddingBottom: 66,
    maxWidth: 800,
    width: '100%',
    alignSelf: 'center',
  },
  inputContainer: {
    marginTop: -50,
  },
  scrollContent: {
    alignItems: 'center',
  },
  messages: {
    gap: 30,
    width: '100%',
  },
  drop: {
    position: 'absolute',
    top: -1,
    right: -1,
    bottom: -1,
    left: -1,
    borderWidth: 2,
    borderRadius: 8,
    borderColor: theme.colors.brand.$4Base,
    overflow: 'visible',
  },
  bottomButtonContainer: {
    position: 'absolute',
    bottom: 66,
    alignSelf: 'center',
    padding: 4,
    borderRadius: 999,
    backgroundColor: theme.colors.neutral.$0,
    boxShadow: '0px 2px 6px 0px rgba(28, 27, 34, 0.05)',
    border: '1px solid rgba(0, 0, 0, 0.10)',
  },
});
